import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../redux/store';
import {
    getContentData,
    getExtendedContentData,
    getProbes,
    getRating,
    getLiteVersionContentData,
} from '../../../api/authApisV2';
import { frameTestQuestions } from '../../../service/QuestionService';
import type { TestTaker } from '../../../interface/interface';
import type { Prompt } from '../../../interface/Prompt';
import type { Probe } from '../../../interface/Probe';
import { fetchConfiguration, configurationsGT } from '../../../constants/configuration';
import { Configuration } from '../../../interface/Configuration';

const calibrationStatusMap: any = { GT: 'LIVE', GT_TRIAL: 'TRIAL' };
const isMiniVersion = String(process.env.REACT_APP_SHOW_MINI_VERSION) === 'true';

export const useLoadTestResource = () => {
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [promptList, setPromptList] = useState<Prompt[]>([]);
    const [probeList, setProbeList] = useState<Probe[]>([]);
    const [ratedContentFetched, setRatedContentFetched] = useState(false);
    const [configuration, setConfiguration] = useState<Configuration>(configurationsGT);
    const [error, setError] = useState<string | null>(null);

    const mediumRatedprompts = useRef<Prompt[]>([]);
    const hardRatedprompts = useRef<Prompt[]>([]);

    const { testTaker, insperaDetails, calibrationDetails } = useSelector((state: RootState) => state.appConfigSlice);
    const isHoelt = insperaDetails.product === 'HOELT';
    const isGT = insperaDetails.product === 'GT_TRIAL' || insperaDetails.product === 'GT';

    const getURLParams = () => {
        const params = new URLSearchParams({
            calibrationStatus: isHoelt ? calibrationDetails.status : calibrationStatusMap[insperaDetails.product],
        });

        if (isHoelt) params.append('calibrationLevel', calibrationDetails.level as string);
        
        return params.toString();
    };

    const fetchContents = async (testTaker: TestTaker) => {
        setLoading(true);

        try {
            const urlParams = getURLParams();
            const productType = insperaDetails?.product === 'GT_TRIAL' ? 'GT' : insperaDetails?.product;
            let calibrationLevel: string | undefined;
            if (isHoelt) {
                calibrationLevel = calibrationDetails.level || undefined;
            }
            const [contentResponse, probesResponse, configuration] = await Promise.all([
                isMiniVersion ? getLiteVersionContentData(testTaker) : getContentData(testTaker, urlParams, productType, calibrationLevel),
                getProbes(testTaker, urlParams, productType),
                fetchConfiguration(insperaDetails.product),
            ]);

            const extendedContentResponse = isMiniVersion
                ? undefined
                : await getExtendedContentData(testTaker, urlParams, productType, calibrationLevel);

            const { questionList, hardQuestionList, mediumQuestionList } = await frameTestQuestions(
                {
                    content: contentResponse.data,
                    extendedContent: extendedContentResponse?.data,
                },
                String(isMiniVersion),
                insperaDetails?.product
            );

            const probeList = probesResponse.data.Probes.sort( //NOSONAR
                (a: Probe, b: Probe) => (a.sequenceOrder > b.sequenceOrder ? 1 : -1),
            );

            mediumRatedprompts.current = mediumQuestionList;
            hardRatedprompts.current = hardQuestionList;

            setPromptList(questionList);
            setProbeList(probeList);
            setConfiguration(configuration);
            setRatedContentFetched(!isGT);

            setInitialized(true);
        } catch (error) {
            setError((error as Error).message ?? 'Error fetching test resources');
        } finally {
            setLoading(false);
        }
    };

    const getPart3RatedContent = () => {
        const rating = getRating();
        const ratedContent = rating > 5 ? hardRatedprompts.current : mediumRatedprompts.current;

        setPromptList([...promptList, ...ratedContent]);
        setRatedContentFetched(true);
    };

    useEffect(() => {
        if (testTaker?.testTakerId) fetchContents(testTaker);
    }, [testTaker?.testTakerId]);

    return {
        testTaker,
        loading,
        initialized,
        promptList,
        probeList,
        ratedContentFetched,
        configuration,
        getPart3RatedContent,
        error,
    };
};
